import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getFallbackTaxYearConfig,
  getTaxYearConfig,
} from '@/services/taxYearApi';

interface TaxYearContextValue {
  /** Default tax year from server */
  defaultYear: number | null;
  /** Currently selected tax year in the UI */
  selectedYear: number;
  /** Set the selected tax year (e.g. from dropdown) */
  setSelectedYear: (year: number) => void;
  /** Supported tax years from the server (newest first) */
  availableYears: number[];
  /** True while tax year config is being fetched */
  isLoading: boolean;
}

const TaxYearContext = createContext<TaxYearContextValue | null>(null);

const STORAGE_KEY = 'tax-filer-selected-tax-year';

function getStoredTaxYear(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    const year = parseInt(raw, 10);
    if (Number.isNaN(year) || !Number.isInteger(year)) return null;
    return year;
  } catch {
    return null;
  }
}

function setStoredTaxYear(year: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(year));
  } catch {
    // ignore
  }
}

/** Newest years first for the dropdown */
function sortYearsDescending(years: number[]): number[] {
  return [...years].sort((a, b) => b - a);
}

interface TaxYearProviderProps {
  children: ReactNode;
}

/**
 * Provides the selected tax year and supported years from the server.
 * Use only in user (non-admin) layout so the year selector is available app-wide.
 */
export function TaxYearProvider({ children }: TaxYearProviderProps) {
  const [defaultYear, setDefaultYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    const stored = getStoredTaxYear();
    return stored ?? new Date().getFullYear() - 1;
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const applyConfig = (
      supportedYears: number[],
      defaultTaxYear: number
    ) => {
      const years = sortYearsDescending(supportedYears);
      setDefaultYear(defaultTaxYear);
      setAvailableYears(years);
      const stored = getStoredTaxYear();
      const useStored = stored !== null && years.includes(stored);
      const yearToUse = useStored ? stored : defaultTaxYear;
      setSelectedYearState(yearToUse);
      if (!useStored) {
        setStoredTaxYear(yearToUse);
      }
    };

    getTaxYearConfig()
      .then((config) => {
        if (!cancelled) {
          applyConfig(config.supportedYears, config.defaultTaxYear);
        }
      })
      .catch(() => {
        if (!cancelled) {
          const fallback = getFallbackTaxYearConfig();
          applyConfig(fallback.supportedYears, fallback.defaultTaxYear);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setSelectedYear = useCallback(
    (year: number) => {
      if (!Number.isInteger(year) || !availableYears.includes(year)) return;
      setSelectedYearState(year);
      setStoredTaxYear(year);
    },
    [availableYears]
  );

  const value = useMemo<TaxYearContextValue>(
    () => ({
      defaultYear,
      selectedYear,
      setSelectedYear,
      availableYears,
      isLoading,
    }),
    [defaultYear, selectedYear, setSelectedYear, availableYears, isLoading]
  );

  return (
    <TaxYearContext.Provider value={value}>
      {children}
    </TaxYearContext.Provider>
  );
}

export function useTaxYear(): TaxYearContextValue {
  const ctx = useContext(TaxYearContext);
  if (!ctx) {
    throw new Error('useTaxYear must be used within TaxYearProvider (user layout only).');
  }
  return ctx;
}

/** Use when component may render outside TaxYearProvider (e.g. shared Header). Returns null if not in provider. */
export function useTaxYearOptional(): TaxYearContextValue | null {
  return useContext(TaxYearContext);
}
